import { RESERVE } from '@sd/content';

/**
 * Reserve currency status (GDD §6). Evaluated once per economic cycle,
 * after the exchange rates. Pure functions only.
 */
export interface ReserveState {
  /** Player whose currency is the reserve, or null. */
  readonly issuer: string | null;
  /** Consecutive cycles each player has met all declaration conditions. */
  readonly streaks: Readonly<Record<string, number>>;
  /** Consecutive cycles the issuer's share has stayed under the loss threshold. */
  readonly lossStreak: number;
}

/** What the reserve check needs to know about each player this cycle. */
export interface ReserveCandidate {
  readonly id: string;
  /** Share of exchange turnover over the rate window, 0..1. */
  readonly share: number;
  /** Output over the rate window, ГС. */
  readonly output: number;
  /** Level of the commerce center, 0 when there is none. */
  readonly commerceLevel: number;
}

export type ReserveChange =
  | { readonly type: 'declared'; readonly playerId: string }
  | { readonly type: 'lost'; readonly playerId: string }
  | null;

export function createReserve(): ReserveState {
  return { issuer: null, streaks: {}, lossStreak: 0 };
}

/** All three conditions, this cycle (GDD §6). */
export function meetsDeclaration(
  candidate: ReserveCandidate,
  all: readonly ReserveCandidate[],
): boolean {
  const total = all.reduce((sum, c) => sum + c.output, 0);
  const largest = all.every((c) => c.id === candidate.id || c.output < candidate.output);
  return (
    candidate.share >= RESERVE.declareShare &&
    largest &&
    total > 0 &&
    candidate.output / total >= RESERVE.declareOutputShare &&
    candidate.commerceLevel >= RESERVE.commerceLevel
  );
}

/**
 * One cycle of the reserve status. While a currency holds the status nobody
 * else can declare; it is lost only after `loseCycles` cycles in a row under
 * `loseShare`, so a share between the two thresholds never flips it.
 */
export function updateReserve(
  reserve: ReserveState,
  candidates: readonly ReserveCandidate[],
): { reserve: ReserveState; change: ReserveChange } {
  if (reserve.issuer !== null) {
    const issuer = candidates.find((c) => c.id === reserve.issuer);
    const under = !issuer || issuer.share < RESERVE.loseShare;
    const lossStreak = under ? reserve.lossStreak + 1 : 0;
    if (lossStreak >= RESERVE.loseCycles) {
      return {
        reserve: createReserve(),
        change: { type: 'lost', playerId: reserve.issuer },
      };
    }
    return { reserve: { ...reserve, lossStreak }, change: null };
  }

  const streaks: Record<string, number> = {};
  for (const c of candidates) {
    streaks[c.id] = meetsDeclaration(c, candidates) ? (reserve.streaks[c.id] ?? 0) + 1 : 0;
  }
  // Only the largest producer can meet the conditions, so at most one streak grows.
  const ready = candidates.find((c) => (streaks[c.id] ?? 0) >= RESERVE.declareCycles);
  if (ready) {
    return {
      reserve: { issuer: ready.id, streaks: {}, lossStreak: 0 },
      change: { type: 'declared', playerId: ready.id },
    };
  }
  return { reserve: { ...reserve, streaks }, change: null };
}
